import { ChevronUp, ChevronDown, X, Maximize2, type LucideIcon } from 'lucide-react';
import { useMemo } from 'react';
import { Button } from '@shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@shared/components/ui/tooltip';
import { cn } from '@shared/lib/utils';
import { PANEL_SPACING } from '../../../../constants/spacing';

const NAVIGATION_TRANSITION = 'transition-all duration-150';

const ENTITY_CONFIG = {
  customer: { display: 'customer', url: 'customers' },
  payment: { display: 'payment', url: 'payments' },
  order: { display: 'order', url: 'orders' },
  merchant: { display: 'merchant', url: 'merchants' }
} as const;

const getEntityConfig = (entityType: string) => {
  return ENTITY_CONFIG[entityType as keyof typeof ENTITY_CONFIG] || {
    display: 'item',
    url: entityType
  };
};

interface NavigationButtonProps {
  icon: LucideIcon;
  onClick?: () => void;
  disabled?: boolean;
  ariaLabel: string;
  tooltip: string;
}

function NavigationButton({ 
  icon: Icon, 
  onClick, 
  disabled, 
  ariaLabel, 
  tooltip 
}: NavigationButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          onClick={onClick}
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn("h-5 w-5 p-0", NAVIGATION_TRANSITION)}
        >
          <Icon className="!h-3 !w-3" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {tooltip}
      </TooltipContent>
    </Tooltip>
  )
}

interface NavigationHeaderProps {
  onBack?: () => void;
  entityType: string;
  onOpenFullDetails?: () => void;
  totalItems: number;
  navigatePrevious?: () => void;
  navigateNext?: () => void;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
}

export function NavigationHeader({
  onBack,
  entityType,
  onOpenFullDetails,
  totalItems,
  navigatePrevious,
  navigateNext,
  canGoPrevious = false,
  canGoNext = false
}: NavigationHeaderProps) {
  const entityConfig = useMemo(() => getEntityConfig(entityType), [entityType]);

  return (
    <div className={cn("h-14 flex items-center px-4 py-2")}>
      <div className={cn("flex items-center", PANEL_SPACING.NAV_ITEM_GAP)}>
        <div className={cn("flex", PANEL_SPACING.NAV_ITEM_GAP)}>
          <NavigationButton
            icon={X}
            onClick={onBack}
            ariaLabel="Close"
            tooltip="Close"
          />

          {onOpenFullDetails && (
            <NavigationButton
              icon={Maximize2}
              onClick={onOpenFullDetails}
              ariaLabel="Open full details"
              tooltip="Open full details"
            />
          )}
        </div>

        {totalItems > 1 && (
          <div className={cn("flex items-center", PANEL_SPACING.NAV_ITEM_GAP)}>
            <NavigationButton
              icon={ChevronUp}
              onClick={navigatePrevious}
              disabled={!canGoPrevious || !navigatePrevious}
              ariaLabel={`Previous ${entityConfig.display}`}
              tooltip={`Previous ${entityConfig.display}`}
            />

            <NavigationButton
              icon={ChevronDown}
              onClick={navigateNext}
              disabled={!canGoNext || !navigateNext}
              ariaLabel={`Next ${entityConfig.display}`}
              tooltip={`Next ${entityConfig.display}`}
            />
          </div>
        )}
      </div>
    </div>
  );
}
